var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { FullScreenContentAtom, InstructionsPopupVisible } from "@/atoms";
import { useAtom } from "jotai";
import { useAtomValue, useUpdateAtom } from "jotai/utils";
import React, { Suspense, useEffect, useMemo, useRef, useState, } from "react";
import { IntlProvider, useIntl } from "react-intl";
import { HashRouter, MemoryRouter, Route, Routes, useNavigate, } from "react-router-dom";
import { CurrentUserPossessArtbook } from "./api/api";
import { ArtbookLoaded, CameraAtom, DownloadPopupVisible, FooterVisible, } from "./atoms";
import styles from "./components/App.module.scss";
import { Artbook } from "./components/Artbook";
import { ArtbookControls } from "./components/ArtbookControls/ArtbookControls";
import { Popin } from "./components/atoms/Popin";
import { DownloadPage } from "./components/DownloadPage";
import { DownloadPopin } from "./components/DownloadPopin";
import { ErrorFallback } from "./components/ErrorFallback";
import { ErrorPage } from "./components/ErrorPage";
import { Instructions, InstructionsReminder, InstructionsReminderWithLanguageSelector, } from "./components/Instructions";
import { Login } from "@/components/Login/Login";
import { LogoFooter } from "@/components/LogoFooter";
import { LogoHeader } from "@/components/LogoHeader";
import { NoArtbook } from "@/components/Login/NoArtbook";
import { getDefaultLocale, isLocaleAvailable, useNavigateToLocale, } from "./locales";
import { useUbisoftUrlLocale } from "./ubisoft/locale";
import { useIsDesktop } from "./utils/useIsDesktop";
import { metrics } from "./analytics/events";
import { CONFIG } from "./config";
import { LoadingPage } from "./components/LoadingPage";
import { Helmet } from "react-helmet";
var Router = process.env.DEBUG_ROUTER_ENABLED === "true" ? HashRouter : MemoryRouter;
export function Bootstrap() {
    return (React.createElement(Router, null,
        React.createElement(PathLocaleProvider, null,
            React.createElement(App, null))));
}
export function PathLocaleProvider(props) {
    var locale = useUbisoftUrlLocale();
    var localeAvailable = useMemo(function () {
        return locale != null && isLocaleAvailable(locale);
    }, [locale]);
    var navigateToLocale = useNavigateToLocale();
    useEffect(function () {
        if (!localeAvailable) {
            navigateToLocale(getDefaultLocale());
        }
    }, [localeAvailable, navigateToLocale]);
    if (!localeAvailable || locale == null) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(IntlProvider, { locale: locale, messages: require("./locs/".concat(locale, ".json")), onError: function (e) {
            if (process.env.NODE_ENV === "production") {
                console.error(e);
            }
        } }, props.children));
}
export function App() {
    var intl = useIntl();
    useEffect(function () {
        function onResize() {
            var uiScale = Math.min(window.innerWidth / 1400, 1);
            document.body.style.setProperty("--ui-scale", uiScale + "");
        }
        window.addEventListener("resize", onResize);
        onResize();
        return function () {
            window.removeEventListener("resize", onResize);
        };
    }, []);
    useEffect(function () {
        if (process.env.CAPTURE_CONTROLS !== "true")
            return;
        var disabled = false;
        function listener(e) {
            if (e.code === "KeyU") {
                disabled = !disabled;
                document.body.style.setProperty("--ui-visibility", disabled ? "hidden" : "visible");
            }
        }
        window.addEventListener("keydown", listener);
        return function () {
            window.removeEventListener("keydown", listener);
        };
    }, []);
    var locale = useUbisoftUrlLocale();
    return (React.createElement(Suspense, { fallback: null },
        React.createElement(Helmet, null,
            React.createElement("title", null, intl.formatMessage({
                defaultMessage: "Skull and Bones",
                id: "wsbOf5",
            }) +
                " | " +
                intl.formatMessage({
                    defaultMessage: "Digital artbook & soundtrack",
                    id: "jCTuVI",
                }))),
        React.createElement("div", { className: styles.app },
            React.createElement(LogoHeader, null),
            React.createElement("div", { className: styles.mainContent },
                React.createElement(Suspense, { fallback: React.createElement(LoadingPage, null) },
                    React.createElement(ErrorFallback, { fallback: React.createElement(ErrorPage, null) },
                        React.createElement(LoginGate, null))),
                React.createElement(LogoFooter, { locale: locale !== null && locale !== void 0 ? locale : "en-us" })))));
}
export var ContentLoader = function () {
    var setArtbookLoaded = useUpdateAtom(ArtbookLoaded);
    useEffect(function () {
        setArtbookLoaded(false);
        return function () { return setArtbookLoaded(true); };
    }, [setArtbookLoaded]);
    return React.createElement(React.Fragment, null);
};
export function Content() {
    var setFooterVisible = useUpdateAtom(FooterVisible);
    var artbookLoaded = useAtomValue(ArtbookLoaded);
    var updateCamera = useUpdateAtom(CameraAtom);
    var _a = __read(useAtom(InstructionsPopupVisible), 2), displayInstructions = _a[0], setDisplayInstructions = _a[1];
    var _b = __read(useState(true), 2), artbookHidden = _b[0], setArtbookHidden = _b[1];
    useEffect(function () {
        return function () {
            setFooterVisible(true);
        };
    }, [setFooterVisible]);
    useEffect(function () {
        if (displayInstructions) {
            var reminderMode = !artbookHidden;
            if (reminderMode) {
                metrics.onReminderTutorialDisplayed();
            }
            else {
                metrics.onMainTutorialDisplayed();
            }
        }
    }, [artbookHidden, displayInstructions]);
    useEffect(function () {
        if (!artbookHidden) {
            metrics.summaryPageDisplayed();
        }
    }, [artbookHidden]);
    var onEnterArtbook = function () {
        setDisplayInstructions(false);
        setArtbookHidden(false);
        updateCamera({ action: "triggerIntro" });
        metrics.clickOnEnter();
    };
    useEffect(function () {
        setFooterVisible(artbookHidden);
        if (artbookHidden) {
            setDisplayInstructions(true);
        }
    }, [setFooterVisible, artbookHidden, setDisplayInstructions]);
    var content = useRef(null);
    var setFullscreenContent = useUpdateAtom(FullScreenContentAtom);
    useEffect(function () {
        if (content.current) {
            setFullscreenContent(content.current);
        }
    }, [setFullscreenContent]);
    var _c = __read(useAtom(DownloadPopupVisible), 2), downloadPopupVisible = _c[0], setDownloadPopupVisible = _c[1];
    return (React.createElement("div", { className: styles.content, ref: content },
        React.createElement(Suspense, { fallback: React.createElement(ContentLoader, null) },
            React.createElement(Artbook, { hidden: artbookHidden }),
            React.createElement(ArtbookControls, { visible: !artbookHidden }),
            React.createElement(DownloadPopin, { isOpen: downloadPopupVisible, onClose: function () { return setDownloadPopupVisible(false); } })),
        React.createElement(Popin, { isOpen: artbookHidden && displayInstructions, size: "lg", closeButton: false },
            React.createElement(Instructions, { loading: !artbookLoaded, onEnter: onEnterArtbook })),
        CONFIG.localeSelectorEnabled && false ? (React.createElement(Popin, { isOpen: !artbookHidden && displayInstructions, size: "lg", onClose: (!artbookHidden && (function () { return setDisplayInstructions(false); })) ||
                undefined, title: React.createElement("span", null), closeButton: false },
            React.createElement(InstructionsReminderWithLanguageSelector, { onClose: function () { return setDisplayInstructions(false); } }))) : (React.createElement(Popin, { isOpen: !artbookHidden && displayInstructions, size: "lg", onClose: (!artbookHidden && (function () { return setDisplayInstructions(false); })) ||
                undefined, title: React.createElement("span", null), closeButton: true },
            React.createElement(InstructionsReminder, null)))));
}
export function LoginGate() {
    var state = useAtomValue(CurrentUserPossessArtbook);
    console.log("POSSESS ARTBOOK", state);
    var navigate = useNavigate();
    var guardDisabled = process.env.SKIP_LOGIN === "true";
    useEffect(function () {
        if (process.env.DEBUG_ROUTER_ENABLED === "true")
            return;
        if (state == null)
            return;
        if (!state.connected) {
            navigate("/");
        }
        else if (state.hasArtbook) {
            navigate("/artbook");
        }
        else {
            navigate("/restricted");
        }
        // No "navigate" dep here, the value is not stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);
    if (state == null) {
        return React.createElement(LoadingPage, null);
    }
    return (React.createElement(Routes, null,
        (guardDisabled || (state && state.connected && state.hasArtbook)) && (React.createElement(Route, { path: "artbook", element: React.createElement(ContentSelector, null) })),
        (guardDisabled || (state && state.connected && !state.hasArtbook)) && (React.createElement(Route, { path: "restricted", element: React.createElement(NoArtbook, null) })),
        React.createElement(Route, { path: "error", element: React.createElement(ErrorPage, null) }),
        React.createElement(Route, { path: "", element: React.createElement(Login, null) })));
}
export function ContentSelector() {
    var isDesktop = useIsDesktop();
    if (isDesktop) {
        return React.createElement(Content, null);
    }
    else {
        return React.createElement(DownloadPage, null);
    }
}
